/* eslint-disable */
import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * AdminKPI — Session 30 KPI dashboard.
 *
 * Reads /api/admin/kpi/current, /api/admin/kpi/definitions, /api/admin/kpi/deliveries
 * from the backend (kpi.py). All calculations live server-side; this file
 * is pure presentation + action wiring.
 */

const CATEGORY_ORDER = [
  "General Métier",
  "Métier Hours",
  "Community & Events",
  "Booking Platform",
];

const STATUS_STYLE = {
  green:  { bg: "#eaf4ec", text: "#2d6a3a", label: "On track" },
  yellow: { bg: "#faf1d8", text: "#8f6a1a", label: "At risk" },
  red:    { bg: "#f8e0e0", text: "#8b2a2a", label: "Behind" },
  gray:   { bg: "#eeeeee", text: "#666666", label: "No data" },
};

function statusOf(m) {
  const pct = m?.pct_to_target;
  if (m?.value === null || m?.value === undefined || pct === null || pct === undefined) return "gray";
  if (pct >= 0.70) return "green";
  if (pct >= 0.40) return "yellow";
  return "red";
}

function fmtValue(m) {
  if (m.value === null || m.value === undefined) return "Data unavailable";
  if (m.unit === "usd") return "$" + Math.round(m.value).toLocaleString();
  if (m.unit === "percent") return (m.value * 100).toFixed(1) + "%";
  return Number(m.value).toLocaleString();
}
function fmtTarget(m) {
  const t = m.target;
  if (t == null) return "—";
  if (m.unit === "usd") return "$" + Math.round(t).toLocaleString();
  if (m.unit === "percent") return (t * 100).toFixed(0) + "%";
  return Number(t).toLocaleString();
}

export default function AdminKPI() {
  const [periodKind, setPeriodKind] = useState("weekly");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [testRecipient, setTestRecipient] = useState("cam@workmetier.com");
  const [showDefinitions, setShowDefinitions] = useState(false);
  const [definitions, setDefinitions] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/kpi/current?period_kind=${periodKind}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to compute KPIs");
    } finally { setLoading(false); }
  };

  const loadDeliveries = async () => {
    try {
      const r = await api.get(`/admin/kpi/deliveries?limit=15`);
      setDeliveries(r.data.deliveries || []);
    } catch {}
  };

  const loadDefinitions = async () => {
    if (definitions) return;
    try {
      const r = await api.get(`/admin/kpi/definitions`);
      setDefinitions(r.data);
    } catch {}
  };

  useEffect(() => { load(); loadDeliveries(); }, [periodKind]);
  useEffect(() => { if (showDefinitions) loadDefinitions(); }, [showDefinitions]);

  const sendTest = async () => {
    if (!testRecipient || !testRecipient.includes("@")) {
      toast.error("Enter a valid recipient email");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(
        `/admin/kpi/send-test?period_kind=${periodKind}&recipient=${encodeURIComponent(testRecipient)}`
      );
      if (r.data.ok) toast.success(`Test ${periodKind} report sent to ${testRecipient}`);
      else toast.error(r.data.error || "Send failed");
      loadDeliveries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  const resend = async (deliveryId) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/kpi/deliveries/${deliveryId}/resend`);
      if (r.data.ok) toast.success("Resent");
      else toast.error(r.data.error || "Resend failed");
      loadDeliveries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally { setBusy(false); }
  };

  const metrics = data?.snapshot?.metrics || {};
  const prior = data?.prior?.metrics || {};
  const summary = data?.summary || { counts: { green:0, yellow:0, red:0, gray:0 } };

  const grouped = useMemo(() => {
    const g = {};
    for (const [k, m] of Object.entries(metrics)) {
      const cat = m.category || "Other";
      if (!g[cat]) g[cat] = [];
      g[cat].push([k, m]);
    }
    return g;
  }, [metrics]);

  const deltaHtml = (key, m) => {
    const pm = prior[key];
    if (!pm || pm.value == null || m.value == null || !pm.value) return null;
    const d = m.value - pm.value;
    const pct = (d / pm.value) * 100;
    const arrow = d > 0 ? "▲" : (d < 0 ? "▼" : "•");
    const color = d > 0 ? "#4a8f5a" : (d < 0 ? "#b04040" : "#8a8a8a");
    return <span style={{color, fontSize:11.5, marginLeft:6}}>{arrow} {Math.abs(pct).toFixed(1)}%</span>;
  };

  return (
    <div className="m-admin-section" data-testid="admin-kpi">
      <div className="m-card" style={{marginBottom:16}}>
        <div style={{display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:12, alignItems:"baseline"}}>
          <div>
            <span className="m-eyebrow" style={{color:"var(--gold)"}}>Métier · Company KPIs</span>
            <h2 className="rec-h2" style={{margin:"6px 0 8px"}}>Goals & reporting.</h2>
            <p className="m-muted small" style={{maxWidth:640, marginTop:-4}}>
              13 leadership KPIs across 4 categories, computed live from Mongo.
              Timezone: America/New_York.
              {data?.snapshot?.computed_at && (
                <> Last refresh: {new Date(data.snapshot.computed_at).toLocaleString()}.</>
              )}
            </p>
          </div>
          <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
            {["weekly","monthly","quarterly"].map(p => (
              <button key={p}
                className={"m-pill light" + (periodKind === p ? " active" : "")}
                onClick={() => setPeriodKind(p)}
                data-testid={`kpi-period-${p}`}
                style={{padding:"6px 12px", fontSize:12}}
              >{p.charAt(0).toUpperCase() + p.slice(1)}</button>
            ))}
            <button className="m-btn-ghost small" onClick={load} disabled={loading} data-testid="kpi-refresh">
              {loading ? "Refreshing…" : "Refresh"}
            </button>
            <button className="m-btn-ghost small" onClick={() => setShowDefinitions(v => !v)} data-testid="kpi-defs-toggle">
              {showDefinitions ? "Hide" : "View"} definitions
            </button>
          </div>
        </div>

        {/* Executive summary strip */}
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))", gap:10, marginTop:18}}>
          {["green","yellow","red","gray"].map(k => (
            <div key={k} style={{
              background: STATUS_STYLE[k].bg, color: STATUS_STYLE[k].text,
              padding:"14px 18px", borderRadius:10,
            }}>
              <div style={{fontFamily:"Georgia,serif", fontSize:28, fontWeight:600, lineHeight:1}}>
                {summary.counts[k] || 0}
              </div>
              <div style={{fontSize:11, letterSpacing:"0.12em", textTransform:"uppercase", marginTop:6, opacity:0.9}}>
                {STATUS_STYLE[k].label}
              </div>
            </div>
          ))}
        </div>

        {/* Test send + delivery status */}
        <div style={{
          marginTop:18, padding:"12px 16px",
          background:"rgba(184,145,58,0.06)",
          border:"1px dashed rgba(184,145,58,0.35)",
          borderRadius:8,
          display:"grid", gap:8, gridTemplateColumns:"1fr auto auto",
          alignItems:"end",
        }}>
          <label className="m-field small" style={{margin:0}}>
            <span>Recipient for test report</span>
            <input type="email" value={testRecipient}
              onChange={e => setTestRecipient(e.target.value)}
              data-testid="kpi-test-recipient" />
          </label>
          <button className="m-btn-gold small" onClick={sendTest} disabled={busy} data-testid="kpi-send-test">
            {busy ? "Sending…" : `Send test ${periodKind} report →`}
          </button>
          <div className="m-muted small" style={{fontSize:11, lineHeight:1.5}}>
            Scheduler:<br/>
            Sun 09:00 ET · monthly 1st · quarterly Q+1
          </div>
        </div>
      </div>

      {/* Category cards */}
      {loading ? (
        <div className="m-loading-inline">Computing KPIs…</div>
      ) : (
        CATEGORY_ORDER.filter(c => grouped[c]).map(cat => (
          <div key={cat} className="m-card" style={{marginBottom:14}}>
            <h3 className="m-side-title" style={{marginTop:0, marginBottom:12}}>{cat}</h3>
            <div style={{display:"flex", flexDirection:"column", gap:0}}>
              {grouped[cat].map(([key, m]) => {
                const s = statusOf(m);
                const st = STATUS_STYLE[s];
                return (
                  <div key={key} data-testid={`kpi-row-${key}`}
                    style={{padding:"12px 4px", borderTop:"1px solid rgba(212,168,75,0.08)", display:"flex", flexDirection:"column", gap:6}}>
                    <div style={{display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:12, alignItems:"baseline"}}>
                      <div style={{flex:"2 1 260px"}}>
                        <div style={{fontSize:14.5, color:"var(--m-text)", fontWeight:500}}>
                          <span style={{
                            display:"inline-block", width:8, height:8, borderRadius:99,
                            background: st.text, marginRight:8, verticalAlign:"middle",
                          }} data-testid={`kpi-status-${key}`}></span>
                          {m.title}
                          {m.extra?.preliminary && (
                            <span style={{fontSize:9, background:"rgba(212,168,75,0.15)", color:"#8a7a3a",
                              padding:"1px 8px", borderRadius:99, marginLeft:8,
                              letterSpacing:"0.1em", textTransform:"uppercase"}}>Preliminary</span>
                          )}
                        </div>
                        {m.incomplete_reason && (
                          <div style={{fontSize:11.5, color:"#c46a3a", marginTop:2}}>⚠ {m.incomplete_reason}</div>
                        )}
                        <div className="m-muted small" style={{marginTop:3, fontSize:11.5}}>{m.definition}</div>
                      </div>
                      <div style={{textAlign:"right", flex:"1 1 160px"}}>
                        <div style={{fontFamily:"Georgia,serif", fontSize:22, color:"var(--m-text)", fontWeight:600, lineHeight:1.1}}>
                          {fmtValue(m)}
                          {deltaHtml(key, m)}
                        </div>
                        <div className="m-muted small" style={{fontSize:11}}>
                          of {fmtTarget(m)} · {m.pct_to_target != null ? (m.pct_to_target * 100).toFixed(1) + "%" : "—"}
                        </div>
                        <span style={{
                          display:"inline-block", marginTop:6,
                          fontSize:10, letterSpacing:"0.14em", textTransform:"uppercase",
                          background: st.bg, color: st.text,
                          padding:"3px 10px", borderRadius:999, fontWeight:600,
                        }}>{st.label}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {/* Delivery log */}
      <div className="m-card" style={{marginTop:14}}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline"}}>
          <h3 className="m-side-title" style={{marginTop:0}}>Recent report deliveries</h3>
          <button className="m-btn-ghost small" onClick={loadDeliveries}>Reload</button>
        </div>
        {deliveries.length === 0 ? (
          <div className="m-empty small">No reports sent yet. Use the "Send test report" button above.</div>
        ) : (
          <table style={{width:"100%", fontSize:13, borderCollapse:"collapse", marginTop:8}}>
            <thead>
              <tr style={{textAlign:"left", color:"var(--m-muted)", borderBottom:"1px solid rgba(212,168,75,0.15)"}}>
                <th style={{padding:"8px 6px"}}>When</th>
                <th style={{padding:"8px 6px"}}>Kind</th>
                <th style={{padding:"8px 6px"}}>Period</th>
                <th style={{padding:"8px 6px"}}>Recipient</th>
                <th style={{padding:"8px 6px"}}>Status</th>
                <th style={{padding:"8px 6px"}}></th>
              </tr>
            </thead>
            <tbody>
              {deliveries.map(d => (
                <tr key={d.delivery_id} style={{borderBottom:"1px solid rgba(212,168,75,0.08)"}}
                  data-testid={`kpi-delivery-${d.delivery_id}`}>
                  <td style={{padding:"8px 6px"}}>{new Date(d.created_at).toLocaleString()}</td>
                  <td style={{padding:"8px 6px", textTransform:"capitalize"}}>{d.period_kind}</td>
                  <td style={{padding:"8px 6px"}}>{d.period_label}</td>
                  <td style={{padding:"8px 6px"}}>{d.recipient}</td>
                  <td style={{padding:"8px 6px"}}>
                    <span style={{
                      display:"inline-block", padding:"2px 8px", borderRadius:99, fontSize:10, letterSpacing:"0.1em",
                      textTransform:"uppercase", fontWeight:600,
                      background: d.status === "sent" ? "#eaf4ec" : "#f8e0e0",
                      color:      d.status === "sent" ? "#2d6a3a" : "#8b2a2a",
                    }}>{d.status}</span>
                    {d.error && <div style={{fontSize:11, color:"#b04040", marginTop:2}}>{d.error}</div>}
                  </td>
                  <td style={{padding:"8px 6px"}}>
                    <button className="m-btn-ghost small" onClick={() => resend(d.delivery_id)} disabled={busy}
                      data-testid={`kpi-resend-${d.delivery_id}`}>Resend</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Definitions panel */}
      {showDefinitions && definitions && (
        <div className="m-card" style={{marginTop:14}} data-testid="kpi-definitions">
          <h3 className="m-side-title" style={{marginTop:0}}>KPI definitions</h3>
          <div className="m-muted small" style={{marginBottom:12}}>
            Single source of truth. Every scheduled email and dashboard row calculates from these queries.
            Recipient: <code>{definitions.default_recipient}</code> · Timezone: <code>{definitions.timezone}</code>.
          </div>
          {Object.entries(definitions.definitions).map(([key, meta]) => (
            <div key={key} style={{padding:"10px 0", borderTop:"1px solid rgba(212,168,75,0.08)"}}>
              <div style={{fontWeight:600, color:"var(--m-text)"}}>{meta.title}
                <span className="m-muted small" style={{marginLeft:8}}>· {meta.category}</span>
              </div>
              <div className="m-muted small" style={{marginTop:4}}>{meta.definition}</div>
              <div className="m-muted small" style={{marginTop:2, fontSize:11, opacity:0.75}}>
                Source: <code>{meta.source}</code>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
